import React from "react";
import Logger from "../../common/logger";
import { getCookie, setCookie, bimmatchSupportEmail } from "../../utils";
// Imported directly (not via the components barrel) so we don't pull the whole
// component index into the entry chunk. This is purely presentational, so it's
// safe to render at the top level - above the Apollo/Auth/Router providers -
// where this boundary lives.
import Button from "../../components/button";

// Matches the errors thrown when a dynamically imported chunk can no longer be
// found on the server. This typically happens right after a new release: the
// browser is still running an older app shell that references chunk filenames
// (content-hashed) which no longer exist on the CDN.
const CHUNK_FAILED_MESSAGE = /Loading (?:CSS )?chunk [\w-]+ failed/i;

// Cookie that records that we already auto-reloaded to recover from a chunk
// failure. It expires after 5 minutes, so we auto-reload at most once per
// 5-minute window - within that window the user must reload manually. The
// cookie's own expiry is what enforces the window: once it lapses getCookie
// returns null, so a user returning to the error screen after 5 minutes gets
// another automatic reload.
const RELOAD_COOKIE = "chunk_error_auto_reloaded";
const RELOAD_WINDOW_IN_DAYS = 5 / (24 * 60); // 5 minutes, expressed in days

const isChunkLoadError = (error) => {
  if (!error) return false;
  return (
    error.name === "ChunkLoadError" ||
    CHUNK_FAILED_MESSAGE.test(error.message || "")
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // A stale-build chunk failure: reload to pick up the latest app shell, but
    // only if we haven't already auto-reloaded within the last 5 minutes.
    if (isChunkLoadError(error)) {
      const reloadedRecently = getCookie(RELOAD_COOKIE) === "true";
      if (!reloadedRecently) {
        // Remember that we auto-reloaded (cookie expires in 5 min), show the
        // "Reloading" screen, then reload.
        setCookie(RELOAD_COOKIE, "true", RELOAD_WINDOW_IN_DAYS);
        this.setState({ reloading: true });
        window.location.reload();
        return;
      }
    }

    // Non-chunk render error, or a chunk error already auto-reloaded within the
    // last 5 minutes: surface it to our logger so we can actually see these in
    // production, and let render() show the manual fallback.
    try {
      Logger.error("ErrorBoundary caught error", { error, info });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught error", error, info);
    }
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (this.state.reloading) {
      // A chunk-error reload is in flight: show a lightweight "Reloading"
      // screen until the page navigates.
      return (
        <div className="error-boundary-fallback">
          <div className="page-loader"></div>
          <h2>Reloading…</h2>
          <p>A new version is available. Refreshing the page…</p>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="error-boundary-fallback">
          <img
            className="error-face"
            src="/images/icon/emoji-surprise.svg"
            alt=""
          />
          <p className="title">Something went wrong</p>
          {/* Plain anchors, not router links - this boundary renders above the
              Router provider, and a full navigation also resets the app state
              that just crashed. */}
          <p className="subtitle">
            Reload the page, or rollback to the <a href="/">app homepage</a>
          </p>
          <Button onClick={this.handleReload}>Reload</Button>
          <p className="support">
            If the problem persists, contact our support at{" "}
            <a href={`mailto:${bimmatchSupportEmail}`}>{bimmatchSupportEmail}</a>
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
